import type { TaskSummary } from '@pm/schemas';
import { Avatar, Badge, cn, PriorityBadge, StatusBadge, Tag, Tooltip } from '@pm/ui';
import { ListTree, Lock } from 'lucide-react';
import { Link } from 'react-router';
import { fmtDue } from '@/lib/format';

/** Prazo em mono com cor por urgência e badge sólido de atraso. */
export function DueCell({
  task,
  className,
}: {
  task: Pick<TaskSummary, 'due_at' | 'is_overdue' | 'status'>;
  className?: string;
}) {
  if (!task.due_at)
    return <span className={cn('font-mono text-sm text-text-3', className)}>—</span>;
  const closed = task.status === 'COMPLETED' || task.status === 'CANCELED';
  const label = fmtDue(task.due_at);
  const today = label.startsWith('hoje');
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 font-mono text-sm',
        task.is_overdue
          ? 'text-danger'
          : today && !closed
            ? 'text-warning'
            : closed
              ? 'text-text-3'
              : 'text-text-1',
        className,
      )}
    >
      {label}
      {task.is_overdue ? (
        <Badge variant="solid" tone="danger">
          {overdueLabel(task.due_at)}
        </Badge>
      ) : null}
    </span>
  );
}

function overdueLabel(iso: string): string {
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  return days >= 1 ? `+${days}d` : 'Atrasada';
}

export function ResponsibleCell({ task }: { task: Pick<TaskSummary, 'responsible'> }) {
  if (!task.responsible) {
    return (
      <span className="inline-flex items-center gap-1.5 text-sm text-warning">
        <span className="grid size-5 place-items-center rounded-full border border-dashed border-warning text-[10px]">
          ?
        </span>
        Não atribuída
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-base">
      <Avatar name={task.responsible.name} size="sm" />
      <span className="truncate">{task.responsible.name}</span>
    </span>
  );
}

export function ParticipantsCell({
  task,
  max = 3,
}: {
  task: Pick<TaskSummary, 'participants'>;
  max?: number;
}) {
  const shown = task.participants.slice(0, max);
  const rest = task.participants.length - shown.length;
  if (task.participants.length === 0) return <span className="text-text-3">—</span>;
  return (
    <span className="inline-flex items-center">
      {shown.map((u, i) => (
        <Tooltip key={u.id} content={u.name}>
          <span className={cn(i > 0 && '-ml-1.5')}>
            <Avatar name={u.name} size="sm" />
          </span>
        </Tooltip>
      ))}
      {rest > 0 ? <span className="ml-1 font-mono text-xs text-text-3">+{rest}</span> : null}
    </span>
  );
}

export function TagsCell({ task, max = 2 }: { task: Pick<TaskSummary, 'tags'>; max?: number }) {
  const shown = task.tags.slice(0, max);
  const rest = task.tags.length - shown.length;
  if (task.tags.length === 0) return null;
  return (
    <span className="inline-flex items-center gap-1">
      {shown.map((t) => (
        <Tag key={t.id} color={t.color ?? undefined}>
          {t.name}
        </Tag>
      ))}
      {rest > 0 ? <span className="font-mono text-xs text-text-3">+{rest}</span> : null}
    </span>
  );
}

export function SubtasksIndicator({
  task,
}: {
  task: Pick<TaskSummary, 'subtasks_total' | 'subtasks_completed'>;
}) {
  if (task.subtasks_total === 0) return null;
  return (
    <span className="inline-flex items-center gap-1 font-mono text-xs text-text-3">
      <ListTree className="size-3" />
      {task.subtasks_completed}/{task.subtasks_total}
    </span>
  );
}

/** Título com link para a tarefa; mostra cadeado em pessoais. */
export function TaskTitle({
  task,
  className,
}: {
  task: Pick<TaskSummary, 'id' | 'title' | 'scope' | 'number' | 'status'>;
  className?: string;
}) {
  const closed = task.status === 'COMPLETED' || task.status === 'CANCELED';
  return (
    <Link
      to={`/tasks/${task.id}`}
      className={cn(
        'inline-flex min-w-0 items-center gap-1.5 font-medium hover:text-accent',
        closed && 'text-text-3',
        className,
      )}
    >
      {task.scope === 'PERSONAL' ? <Lock className="size-3 shrink-0 text-text-3" /> : null}
      <span className="truncate">{task.title}</span>
    </Link>
  );
}

/** Linha compacta usada em Meu Trabalho e Kanban. */
export function TaskCompactRow({ task }: { task: TaskSummary }) {
  return (
    <div className="flex h-row items-center gap-3 border-b border-border px-4 last:border-b-0 hover:bg-surface-2">
      <span className="w-14 shrink-0 font-mono text-xs text-text-3">#{task.number}</span>
      <TaskTitle task={task} className="min-w-0 flex-1" />
      {task.context ? (
        <span className="hidden truncate text-sm text-text-2 md:block md:max-w-[160px]">
          {task.context.name}
        </span>
      ) : null}
      <SubtasksIndicator task={task} />
      <StatusBadge status={task.status} />
      <PriorityBadge priority={task.priority} />
      <DueCell task={task} className="w-[120px] justify-end" />
    </div>
  );
}
